import { AuthEndpoint, accountDetailsEndpoint, EncryptedEndpoint } from '@datarock/api-client';
import { VUserType } from '@datarock/protobuffers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createGlobalState } from 'react-hooks-global-state';
import useAuth from './useAuth';
import { useController } from '@data-client/react';
import useStorageContext from './useStorageContext';
const KEY_NAME = 'accounts';
/** How long until licensing is considered stale and should be refetched (24h) */
const LICENSING_STALE_MS = 24 * 60 * 60 * 1000;
/** Backoff after failed fetch before retrying (5 min) */
const LICENSING_RETRY_BACKOFF_MS = 5 * 60 * 1000;
function needsLicensingRefresh(account, getAccessToken) {
    if (!getAccessToken(account.email))
        return false;
    if (!account.licensing) {
        if (account.licensingFetchAttemptedAt &&
            Date.now() - account.licensingFetchAttemptedAt < LICENSING_RETRY_BACKOFF_MS) {
            return false;
        }
        return true;
    }
    if (!account.licensingFetchedAt)
        return true;
    return Date.now() - account.licensingFetchedAt > LICENSING_STALE_MS;
}
const { getGlobalState, setGlobalState, useGlobalState } = createGlobalState({
    accounts: null
});
function addAccount(account) {
    const s = getGlobalState(KEY_NAME);
    if (!s) {
        setGlobalState(KEY_NAME, [account]);
        return;
    }
    if (s.find((a) => a.email === account.email)) {
        return;
    }
    const updated = s.concat([account]);
    setGlobalState(KEY_NAME, updated);
}
function removeAccount(account) {
    const s = getGlobalState(KEY_NAME);
    if (!s) {
        return;
    }
    const index = s.findIndex((a) => a.email === account.email);
    if (index >= 0) {
        const updated = s.slice(0);
        updated.splice(index, 1);
        setGlobalState(KEY_NAME, updated);
    }
}
function updateAccount(account) {
    const s = getGlobalState(KEY_NAME);
    if (!s) {
        return;
    }
    const index = s.findIndex((a) => a.email === account.email);
    if (index >= 0) {
        const updated = s.slice(0);
        updated.splice(index, 1, account);
        setGlobalState(KEY_NAME, updated);
    }
}
export default function useAccounts() {
    const { getAccessToken, clearAccessToken } = useAuth();
    const storage = useStorageContext();
    const [state, setState] = useGlobalState(KEY_NAME);
    const { fetch } = useController();
    useEffect(() => {
        if (state) {
            const str = JSON.stringify(state);
            storage.setItem(KEY_NAME, str);
            return;
        }
        const str = storage.getItem(KEY_NAME);
        if (str) {
            const acs = JSON.parse(str);
            setState(acs);
        }
        else {
            setState([]);
        }
    }, [setState, state, storage]);
    const memoizedFetch = useMemo(() => fetch, [fetch]);
    const refreshInFlightRef = useRef(new Set());
    const [refreshTick, setRefreshTick] = useState(0);
    const fetchLicensing = useCallback(async (account, accessToken = undefined) => {
        var _a, _b;
        if (accessToken) {
            AuthEndpoint.accessToken = accessToken;
        }
        else {
            const token = getAccessToken(account.email);
            if (!token) {
                return account;
            }
            AuthEndpoint.accessToken = token.accessToken;
        }
        const now = Date.now();
        try {
            const response = await memoizedFetch(accountDetailsEndpoint);
            if (response.userDetails) {
                const updated = Object.assign({}, account);
                updated.licensing = (_a = response.userDetails) === null || _a === void 0 ? void 0 : _a.licensing;
                updated.licensingFetchedAt = now;
                updated.licensingFetchAttemptedAt = now;
                switch ((_b = response.userDetails) === null || _b === void 0 ? void 0 : _b.userType) {
                    case VUserType.FREE:
                        updated.userType = 'free';
                        break;
                    case VUserType.LICENSED:
                        updated.userType = 'licensed';
                        break;
                    default:
                        updated.userType = undefined;
                        break;
                }
                return updated;
            }
        }
        catch (error) {
            console.log('Error fetching details', error);
        }
        // No userDetails in response or fetch failed - set attempted timestamp to prevent retry loop
        return Object.assign({}, account, { licensingFetchAttemptedAt: now });
    }, [getAccessToken, memoizedFetch]);
    const removeAccountAndClearToken = useCallback((account) => {
        removeAccount(account);
        clearAccessToken(account.email);
    }, [clearAccessToken]);
    // Refs to avoid effect re-running when callbacks change (can cause loops with DataClient)
    const fetchLicensingRef = useRef(fetchLicensing);
    const getAccessTokenRef = useRef(getAccessToken);
    fetchLicensingRef.current = fetchLicensing;
    getAccessTokenRef.current = getAccessToken;
    // Periodic tick to re-check accounts needing licensing refresh (e.g. after retry backoff)
    useEffect(() => {
        const tick = () => setRefreshTick((t) => t + 1);
        const id = setInterval(tick, LICENSING_RETRY_BACKOFF_MS);
        return () => clearInterval(id);
    }, []);
    // Refresh licensing for accounts that need it (missing, failed, or stale).
    // Only depends on state and refreshTick to avoid loops from unstable callback refs.
    useEffect(() => {
        if (!state || state.length === 0)
            return;
        // accountDetailsEndpoint requires encryption keys - skip if not yet set (e.g. Encrypt tab before useKeys loads)
        const keys = EncryptedEndpoint.keys;
        if (!(keys === null || keys === void 0 ? void 0 : keys.symmetricKey) || !(keys === null || keys === void 0 ? void 0 : keys.signingKey) || !(keys === null || keys === void 0 ? void 0 : keys.sessionId)) {
            return;
        }
        const refreshAccount = async (account) => {
            if (refreshInFlightRef.current.has(account.email))
                return;
            refreshInFlightRef.current.add(account.email);
            try {
                const result = await fetchLicensingRef.current(account);
                updateAccount(result);
            }
            finally {
                refreshInFlightRef.current.delete(account.email);
            }
        };
        state
            .filter((a) => needsLicensingRefresh(a, getAccessTokenRef.current))
            .forEach((a) => refreshAccount(a));
    }, [state, refreshTick]);
    return useMemo(() => ({
        accounts: state,
        addAccount,
        fetchLicensing,
        removeAccount: removeAccountAndClearToken,
        updateAccount
    }), [fetchLicensing, removeAccountAndClearToken, state]);
}
